import type { DynamicPricingCalendarDay } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { dayLabel } from '../month/monthEditorModel';
import { automationCell } from './automationModel';

const SOURCE = {
  Automation: { label: 'Automatik', className: 'bg-forest-soft text-forest-deep' },
  Manual: { label: 'manuell', className: 'bg-sand text-ink-soft' },
  Prefill: { label: 'Standardpreis', className: 'bg-card-alt text-ink-soft' },
  none: { label: '—', className: 'bg-card-alt text-ink-soft' },
} as const;

/** Ein Tag in „Preisautomatik in der Auswahl“: Preis, Quelle, Ziel oder Stand, „wieder automatisch“. */
export function SelectionAutomationDay({
  day,
  onRelease,
  busy,
}: {
  day: DynamicPricingCalendarDay;
  onRelease?: () => void;
  busy: boolean;
}) {
  const cell = automationCell(day);
  const source = SOURCE[day.rateSource ?? 'none'];
  // „bestätigt“ gibt es nur für das, was die Automatik gesendet hat.
  const tracked = day.rateSource === 'Automation' || day.publication !== null;
  const failed = cell.publication === 'failed' ? ' · fehlgeschlagen' : '';
  const note =
    cell.targetRate != null && day.rateSource !== 'Manual'
      ? `Ziel ${cell.targetRate} €${cell.publication === 'sent' ? ' · gesendet' : failed}`
      : day.rateSource === 'Manual'
        ? 'von Hand gesetzt'
        : !tracked
          ? 'ohne Automatik-Wert'
          : day.confirmedRate !== null
            ? `bestätigt${failed}`
            : `noch nicht bestätigt${failed}`;
  return (
    <div className="flex flex-wrap items-center gap-2 text-[13px]">
      <span className="w-[70px] font-mono text-xs">{dayLabel(day.date).replace(/\s\S+$/, '')}</span>
      <span className="whitespace-nowrap font-bold">
        {day.offeringState === 'booked' ? 'gebucht' : `${day.currentRate ?? '—'} €`}
      </span>
      <span
        className={cn(
          'rounded-pill flex h-[22px] items-center px-2 text-[11px] font-bold',
          source.className,
        )}
      >
        {source.label}
      </span>
      <span className="text-ink-soft min-w-0 grow text-xs">{note}</span>
      {onRelease && (
        <button
          type="button"
          disabled={busy}
          onClick={onRelease}
          className="text-forest min-h-11 text-xs font-bold disabled:opacity-50"
        >
          wieder automatisch
        </button>
      )}
    </div>
  );
}
